import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  Group,
  Loader,
  Modal,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { fetcher } from "../../../services/fetcher";
import { SalesLeads_Api } from "../../../api/endpoints";
import { errorNotifications, successNotifications } from "../../../utils/handleNotifications";
import { useLazyAssignPlans } from "../hooks/useLazyAssignPlans";
import { planIdFromLead, planMeta, planTitle, unwrapLead } from "../utils/leadFormUtils";

const QUOTE_TYPES = [
  { value: "B2C", label: "B2C" },
  { value: "B2B", label: "B2B" },
];

const INITIAL_FORM = {
  customer_name: "",
  phone: "",
  email: "",
  cover_id: "",
  trek: "",
  notes: "",
};

const DEFAULT_PRICING = {
  persons: 1,
  start_date: dayjs().format("YYYY-MM-DD"),
  end_date: dayjs().add(1, "day").format("YYYY-MM-DD"),
  nationality: "Indian",
  travelling_from: "India",
  travelling_to: "India",
  traveller_ages: [30],
};

const fmtDate = (v) => {
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
};

const toPickerDate = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.startOf("day").toDate() : null;
};

const pickLeadDate = (source, keys) => {
  if (!source) return null;
  for (const key of keys) {
    const parsed = toPickerDate(source[key]);
    if (parsed) return parsed;
  }
  return null;
};

const START_DATE_KEYS = [
  "start_date",
  "activity_start_date",
  "trek_start_date",
  "travel_start_date",
  "journey_start_date",
];
const END_DATE_KEYS = [
  "end_date",
  "activity_end_date",
  "trek_end_date",
  "travel_end_date",
  "journey_end_date",
];

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.customize_covers)) return data.customize_covers;
  return [];
};

const notify = (message) =>
  notifications.show({ title: "Required", message, color: "orange" });

const pricingFromLead = (lead, dateSource = lead) => {
  if (!lead || (!lead.id && !lead.name)) return { ...DEFAULT_PRICING };
  const rawAges = lead.traveller_ages;
  const ages = Array.isArray(rawAges)
    ? rawAges.map(Number).filter((n) => !Number.isNaN(n))
    : String(rawAges || "")
        .split(",")
        .map((v) => Number(v.trim()))
        .filter((v) => !Number.isNaN(v));

  return {
    persons: lead.persons ?? lead.no_of_people ?? lead.pax ?? DEFAULT_PRICING.persons,
    start_date:
      fmtDate(pickLeadDate(dateSource, START_DATE_KEYS)) || DEFAULT_PRICING.start_date,
    end_date: fmtDate(pickLeadDate(dateSource, END_DATE_KEYS)) || DEFAULT_PRICING.end_date,
    nationality: lead.nationality || DEFAULT_PRICING.nationality,
    travelling_from: lead.travelling_from || DEFAULT_PRICING.travelling_from,
    travelling_to: lead.travelling_to || DEFAULT_PRICING.travelling_to,
    traveller_ages: ages.length ? ages : DEFAULT_PRICING.traveller_ages,
  };
};

const NewQuotationModal = ({ opened, onClose, leads = [], onCreated }) => {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [quoteType, setQuoteType] = useState("B2C");
  const [form, setForm] = useState(INITIAL_FORM);
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [linkedLeadId, setLinkedLeadId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { plans, isPlansLoading, loadPlans, resetPlansLoad } = useLazyAssignPlans();

  useEffect(() => {
    if (!opened) resetPlansLoad();
  }, [opened, resetPlansLoad]);

  useEffect(() => {
    if (!linkedLeadId || !plans.length || !form.trek) return;
    const id = planIdFromLead({ trek: form.trek, plan_name: form.trek }, plans);
    if (id && id !== form.cover_id) setForm((f) => ({ ...f, cover_id: id }));
  }, [linkedLeadId, plans, form.trek, form.cover_id]);

  const leadOptions = useMemo(
    () =>
      leads
        .filter((l) => l?.id)
        .map((l) => ({
          value: String(l.id),
          label: `${l.name || l.lead_name || l.customer_name || "Lead"}${l.lead_no ? ` (${l.lead_no})` : ""}`,
        })),
    [leads]
  );

  const planValue = form.cover_id || form.trek || null;
  const planOptions = useMemo(() => {
    const options = plans.map((p) => ({
      value: String(p.id),
      label: planTitle(p),
      meta: planMeta(p) || undefined,
    }));
    if (planValue && !options.some((o) => o.value === planValue)) {
      options.unshift({ value: planValue, label: form.trek || planValue });
    }
    return options;
  }, [plans, planValue, form.trek]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const reset = () => {
    setForm(INITIAL_FORM);
    setPricing(DEFAULT_PRICING);
    setLinkedLeadId("");
    setQuoteType("B2C");
    resetPlansLoad();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const applyLeadToForm = (lead) => {
    if (!lead) return;
    const trek = String(
      lead.trek ||
        lead.plan_name ||
        lead.activity ||
        lead.cover_name ||
        (lead.cover && typeof lead.cover === "object" ? planTitle(lead.cover) : "") ||
        ""
    ).trim();
    setForm((f) => ({
      ...f,
      customer_name: lead.name || lead.lead_name || lead.customer_name || f.customer_name,
      phone: lead.phone || f.phone,
      email: lead.email || f.email,
      trek,
      cover_id: planIdFromLead(lead, plans),
    }));
  };

  const handleLeadLink = async (value) => {
    setLinkedLeadId(value || "");
    if (!value) {
      setPricing(DEFAULT_PRICING);
      setForm((f) => ({ ...f, cover_id: "", trek: "" }));
      return;
    }
    loadPlans();
    const fromList = leads.find((l) => String(l.id) === value);
    let lead = unwrapLead(fromList || {});
    try {
      lead = unwrapLead({ ...lead, ...unwrapLead(await fetcher(`${SalesLeads_Api}${value}/`)) });
    } catch {
      /* keep list row */
    }

    let dateSource = lead;
    if (!pickLeadDate(lead, START_DATE_KEYS) || !pickLeadDate(lead, END_DATE_KEYS)) {
      try {
        const qRows = normalizeList(await fetcher(`crm/leads/${value}/quotations/`));
        const latest = qRows.find(
          (q) => pickLeadDate(q, START_DATE_KEYS) || pickLeadDate(q, END_DATE_KEYS)
        );
        if (latest) dateSource = { ...lead, ...latest };
      } catch {
        /* no quotation fallback */
      }
    }

    applyLeadToForm(lead);
    setPricing(pricingFromLead(lead, dateSource));
  };

  const handleCreate = async () => {
    const coverId = form.cover_id || planIdFromLead({ trek: form.trek }, plans);
    if (!coverId) return notify("Select a cover plan");
    if (!form.customer_name?.trim()) return notify("Enter customer name");
    if (!form.phone?.trim()) return notify("Enter phone number");
    const plan = plans.find((p) => String(p.id) === coverId);
    const trek = (plan ? planTitle(plan) : form.trek)?.trim();
    if (!trek) return notify("Enter activity name");
    setIsSubmitting(true);
    try {
      await fetcher("crm/quotations/", "POST", {
        quotation_type: quoteType,
        lead: linkedLeadId ? Number(linkedLeadId) : null,
        customer_name: form.customer_name,
        phone: form.phone,
        email: form.email,
        cover: Number(coverId),
        plan_name: trek,
        trek,
        persons: pricing.traveller_ages.length || Number(pricing.persons || 0),
        traveller_ages: pricing.traveller_ages,
        nationality: pricing.nationality,
        travelling_from: pricing.travelling_from,
        travelling_to: pricing.travelling_to,
        notes: form.notes,
      });
      successNotifications("Quotation created");
      if (onCreated) await onCreated();
      handleClose();
    } catch (err) {
      errorNotifications(err?.response?.data || "Failed to create quotation", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered={!isMobile}
      fullScreen={isMobile}
      size={isMobile ? undefined : "lg"}
      title={<Title order={isMobile ? 4 : 3}>New Quotation</Title>}
      styles={{
        content: isMobile ? undefined : { maxWidth: 720 },
        body: { paddingTop: isMobile ? 12 : undefined },
      }}
    >
      <ScrollArea.Autosize mah={isMobile ? "calc(100dvh - 110px)" : "75vh"} offsetScrollbars>
        <Stack gap="md">
          <Stack gap="sm">
            <SegmentedControl
              value={quoteType}
              onChange={setQuoteType}
              data={QUOTE_TYPES}
              radius="xl"
              w={isMobile ? "100%" : "fit-content"}
            />
            <Select
              label="Link to Lead (optional)"
              placeholder="— No lead —"
              data={leadOptions}
              searchable
              clearable
              value={linkedLeadId}
              onChange={handleLeadLink}
              w="100%"
            />
          </Stack>

          <Text size="xs" c="dimmed" fw={600}>CUSTOMER INFO</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Customer Name" placeholder="Enter Customer Name" required value={form.customer_name} onChange={(e) => setField("customer_name", e.target.value)} />
            <TextInput label="Phone" placeholder="Enter Phone Number" required value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
            <TextInput label="Email" placeholder="Enter Email" value={form.email} onChange={(e) => setField("email", e.target.value)} />
          </SimpleGrid>

          {/* <Text size="xs" c="dimmed" fw={600}>ACTIVITY & JOURNEY</Text> */}
          <Select
            label="Plan Name"
            placeholder="Search plans..."
            data={planOptions}
            searchable
            required
            value={planValue}
            onChange={(value) => {
              const plan = plans.find((p) => String(p.id) === value);
              setForm((f) => ({
                ...f,
                cover_id: value || "",
                trek: plan ? planTitle(plan) : "",
              }));
            }}
            onDropdownOpen={loadPlans}
            rightSection={isPlansLoading ? <Loader size="xs" /> : null}
            renderOption={({ option }) => (
              <Stack gap={2} py={2}>
                <Text size="sm" lh={1.3}>{option.label}</Text>
                {option.meta ? (
                  <Text size="xs" c="dimmed" lh={1.2}>{option.meta}</Text>
                ) : null}
              </Stack>
            )}
          />
          {/* <TextInput
            label="Activity Name"
            placeholder="Enter Activity Name"
            value={form.trek}
            onChange={(e) => setField("trek", e.target.value)}
          /> */}

          <Textarea label="Notes" placeholder="Internal notes..." value={form.notes} onChange={(e) => setField("notes", e.target.value)} minRows={2} />
          <Group grow={isMobile}>
            <Button variant="default" onClick={handleClose} fullWidth={isMobile}>
              Cancel
            </Button>
            <Button color="orange" onClick={handleCreate} loading={isSubmitting} fullWidth={isMobile}>
              Create Quotation
            </Button>
          </Group>
        </Stack>
      </ScrollArea.Autosize>
    </Modal>
  );
};

export default NewQuotationModal;
